import React, { useState, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { authFetch, API_BASE } from "../api";
import CredentialsTestModal from "./CredentialsTestModal";
import CreateFromRecipeModal from "./CreateFromRecipeModal";
import "./IntegrationsList.css";

/**
 * Slugify a heading text the same way GitHub / typical MD processors do:
 * lowercase, replace spaces with hyphens, strip everything that isn't
 * [a-z0-9-]. Used to match anchors emitted by our error classifier
 * (e.g. "step-3-create-a-client-secret") against actual markdown headings.
 */
function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/**
 * After the markdown is rendered, scroll to the heading whose text
 * slugifies to the given anchor. react-markdown doesn't emit id attributes
 * by default, so we walk headings and compare.
 */
function scrollToMarkdownHeading(anchor) {
  if (!anchor) return;
  const target = slugify(anchor);
  const container = document.querySelector(".integrations-markdown");
  if (!container) return;
  const headings = container.querySelectorAll("h1, h2, h3, h4, h5, h6");
  for (const h of headings) {
    if (slugify(h.textContent) === target) {
      h.scrollIntoView({ behavior: "smooth", block: "start" });
      // Brief highlight so the user knows where they landed
      h.classList.add("integrations-heading-highlight");
      setTimeout(() => h.classList.remove("integrations-heading-highlight"), 2000);
      return;
    }
  }
}

/**
 * Integrations catalog page.
 *
 * Shows curated integration recipes (Power BI, Salesforce, etc.) that come
 * with pre-built templates, setup guides, and credential validators.
 *
 * This PR (framework only) implements:
 *   - Recipe list view fetched from /api/recipes
 *   - Recipe detail view showing manifest + rendered setup.md
 *   - "Coming soon" affordances for Test Credentials and Create Dashboard
 *     (those hook up in PR 2 alongside the first real recipe)
 */
export default function IntegrationsList({ onNavigateToApp, pendingNav, onPendingNavConsumed } = {}) {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Modal visibility — anchored to the detail view's currently-loaded recipe
  const [showTestModal, setShowTestModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // When a credentials test passes, stash the values here so Create Dashboard
  // can auto-save them to the new app's secret store. Cleared when the user
  // navigates to a different recipe.
  const [verifiedSecrets, setVerifiedSecrets] = useState(null);

  // Fetch the recipe list on mount
  useEffect(() => {
    let cancelled = false;
    authFetch(`${API_BASE}/recipes`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setRecipes(data.recipes || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load integrations");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch full detail (with setup.md) when a recipe is selected.
  // Optional `scrollAnchor` scrolls to a heading (by slugified text) after
  // the markdown renders — used when the user lands here from an error link.
  const openRecipe = useCallback(async (id, scrollAnchor) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    // New recipe selected — any stashed test values are no longer relevant
    setVerifiedSecrets(null);
    try {
      const res = await authFetch(`${API_BASE}/recipes/${id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDetail(data);
      // After the setup.md renders, find the heading whose text matches the
      // anchor (slugified) and scroll it into view.
      if (scrollAnchor) {
        setTimeout(() => {
          scrollToMarkdownHeading(scrollAnchor);
        }, 100);
      }
    } catch (err) {
      setError(err.message || "Failed to load recipe");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Consume pendingNav from parent whenever it changes. This lets the error
  // classifier link in the chat jump directly into a recipe's setup guide.
  // We intentionally only depend on pendingNav — openRecipe is stable enough
  // and onPendingNavConsumed should only fire on new nav requests.
  useEffect(() => {
    if (!pendingNav || !pendingNav.recipeId) return;
    openRecipe(pendingNav.recipeId, pendingNav.docsAnchor);
    if (onPendingNavConsumed) onPendingNavConsumed();
  }, [pendingNav, openRecipe, onPendingNavConsumed]);

  const closeDetail = useCallback(() => {
    setSelectedId(null);
    setDetail(null);
    setVerifiedSecrets(null);
  }, []);

  // ─── Detail view ──────────────────────────────────────────────
  if (selectedId) {
    return (
      <div className="integrations-page">
        <div className="integrations-detail-header">
          <button className="integrations-back" onClick={closeDetail}>← Back to integrations</button>
        </div>

        {detailLoading && <div className="integrations-empty">Loading…</div>}

        {detail && (
          <div className="integrations-detail">
            <div className="integrations-detail-title">
              <h1>{detail.name}</h1>
              {detail.description && <p className="integrations-detail-desc">{detail.description}</p>}
              <div className="integrations-detail-meta">
                {detail.difficulty && (
                  <span className={`integrations-badge integrations-badge-${detail.difficulty}`}>
                    {detail.difficulty}
                  </span>
                )}
                {detail.minSetupMinutes && (
                  <span className="integrations-meta-item">⏱ {detail.minSetupMinutes} min setup</span>
                )}
                {Array.isArray(detail.requiredSecrets) && detail.requiredSecrets.length > 0 && (
                  <span className="integrations-meta-item">🔑 {detail.requiredSecrets.length} keys</span>
                )}
              </div>
            </div>

            <div className="integrations-detail-actions">
              {detail.hasTest && (
                <button className="integrations-action-btn" onClick={() => setShowTestModal(true)}>
                  Test Credentials
                </button>
              )}
              {detail.hasTemplates && (
                <button className="integrations-action-btn" onClick={() => setShowCreateModal(true)}>
                  Create Dashboard
                </button>
              )}
              {!detail.hasTest && !detail.hasTemplates && (
                <span className="integrations-meta-item">This recipe has no automated actions yet.</span>
              )}
            </div>

            {detail.prerequisites && detail.prerequisites.length > 0 && (
              <div className="integrations-section">
                <h2>Prerequisites</h2>
                <ul>
                  {detail.prerequisites.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}

            {Array.isArray(detail.requiredSecrets) && detail.requiredSecrets.length > 0 && (
              <div className="integrations-section">
                <h2>Required API Keys</h2>
                <table className="integrations-keys-table">
                  <thead>
                    <tr><th>Key Name</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    {detail.requiredSecrets.map((s, i) => (
                      <tr key={i}>
                        <td><code>{s.name}</code></td>
                        <td>{s.description || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {detail.setup && (
              <div className="integrations-section">
                <h2>Setup Guide</h2>
                <div className="integrations-markdown">
                  <ReactMarkdown>{detail.setup}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modals — rendered at the page root so they overlay properly */}
        {showTestModal && detail && (
          <CredentialsTestModal
            recipe={detail}
            initialValues={verifiedSecrets || {}}
            onPass={(values) => {
              // Stash the exact values that just validated — Create Dashboard
              // will auto-save them as secrets on the new app
              setVerifiedSecrets(values);
            }}
            onClose={() => setShowTestModal(false)}
          />
        )}
        {showCreateModal && detail && (
          <CreateFromRecipeModal
            recipe={detail}
            verifiedSecrets={verifiedSecrets}
            onClose={() => setShowCreateModal(false)}
            onCreated={(app) => {
              setShowCreateModal(false);
              // Once the app is live the stashed secrets are no longer needed
              setVerifiedSecrets(null);
              if (onNavigateToApp) onNavigateToApp(app);
            }}
          />
        )}
      </div>
    );
  }

  // ─── List view ───────────────────────────────────────────────
  return (
    <div className="integrations-page">
      <header className="integrations-header">
        <h1>Integrations</h1>
        <p className="integrations-subtitle">
          Pre-built recipes for complex integrations — with setup guides and credential validators.
        </p>
      </header>

      {loading && <div className="integrations-empty">Loading…</div>}

      {error && (
        <div className="integrations-empty integrations-error">
          Failed to load integrations: {error}
        </div>
      )}

      {!loading && !error && recipes.length === 0 && (
        <div className="integrations-empty">
          <h2>No integrations yet</h2>
          <p>Curated integration recipes are coming soon. The first one will be Power BI.</p>
        </div>
      )}

      {!loading && recipes.length > 0 && (
        <div className="integrations-grid">
          {recipes.map((r) => (
            <button key={r.id} className="integrations-card" onClick={() => openRecipe(r.id)}>
              <div className="integrations-card-header">
                <span className="integrations-card-title">{r.name}</span>
                {r.difficulty && (
                  <span className={`integrations-badge integrations-badge-${r.difficulty}`}>
                    {r.difficulty}
                  </span>
                )}
              </div>
              {r.description && <p className="integrations-card-desc">{r.description}</p>}
              <div className="integrations-card-footer">
                {r.minSetupMinutes && <span>⏱ {r.minSetupMinutes} min</span>}
                {Array.isArray(r.requiredSecrets) && (
                  <span>🔑 {r.requiredSecrets.length} keys</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
